import { difference } from 'lodash';
import { createSelector } from 'reselect';

import { selectConfigEngines } from '@common-modules/engines/selectors';
import { TdoTask } from '../models/DetailTDO';
import { ViewSettings } from '../models';
import { namespace } from '../store';
import { DetectionAssetState } from '../store.models';

import { DAG_REFERENCE_IDS } from '@helpers/constants';
import moment from 'moment';
import { selectStore as selectVoiceMaskStore } from '../../engines/voice-mask';
import { selectStore as selectSpeakerSeparationStore } from '../../engines/speaker-separation';

const selectStore = (s: { [namespace]: DetectionAssetState & ViewSettings }) =>
  s[namespace];

const selectTdo = createSelector(selectStore, (s) => s.tdo);

/**
 * Select TDO Tasks (up to default Graphql limit).
 */
export const selectTdoTasks = createSelector(
  selectTdo,
  (tdo) => tdo?.tasks?.records || []
);

/**
 * Select TDO Tasks for Redact's engines.
 */
export const selectTaskHistory = createSelector(
  selectTdoTasks,
  selectConfigEngines,
  (tasks, engines) => {
    const engineIds = Object.values(engines);
    return (tasks || []).filter((task) =>
      engineIds.some(
        (id) => task.engineId === id || task.engine.categoryId === id
      )
    );
  }
);

const COMPLETE_STATUSES: ReadonlyArray<string> = ['complete'];
const FAILED_STATUSES: ReadonlyArray<string> = [
  'aborted',
  'cancelled',
  'failed',
];

/**
 * Select completed tasks.
 */
export const selectCompletedTasks = createSelector(selectTaskHistory, (tasks) =>
  (tasks || []).filter((task) => COMPLETE_STATUSES.includes(task.status))
);

/**
 * Select failed tasks.
 */
export const selectFailedTasks = createSelector(selectTaskHistory, (tasks) =>
  (tasks || []).filter((task) => FAILED_STATUSES.includes(task.status))
);

/**
 * Select running tasks.
 */
export const selectRunningTasks = createSelector(
  selectTaskHistory,
  selectCompletedTasks,
  selectFailedTasks,
  (tasks, done, fail) => difference(tasks || [], [...done, ...fail]) || []
);

// This will only take most recent task and return if running
// export const selectMostRecentTaskByEngineIfRunning = createSelector(
//   selectTaskHistory,
//   selectCompletedTasks,
//   selectFailedTasks,
// (tasks, done, fail) => {
//   const tasksGroupByEngine = groupBy(tasks, (s) => s.engineId);
//   const latestLists = [];
//   for (const p in tasksGroupByEngine) {
//     if (Object.prototype.hasOwnProperty.call(tasksGroupByEngine, p)) {
//       const element = tasksGroupByEngine[p][0];
//       latestLists.push(element);
//     }
//   }
//   return difference(latestLists, [...done, ...fail]);
// }
// );

// /**
//  * Select Head Detection engines running tasks.
//  * @returns {Array<Task>}
//  */
export const selectDetectionRunning = createSelector(
  selectConfigEngines,
  selectRunningTasks,
  ({ detectionCategory: id }, tasks) =>
    (tasks || []).filter(
      (task) =>
        task.engine.categoryId === id ||
        task.ioFolders?.some(
          (folder) => folder.referenceId === DAG_REFERENCE_IDS['owFromHead']
        )
    )
);

// /**
//  * Select Face Detection engines failed tasks.
//  * @returns {Array<Task>}
//  */
export const selectDetectionFailed = createSelector(
  selectConfigEngines,
  selectFailedTasks,
  ({ detectionCategory: id }, tasks) =>
    (tasks || []).filter((task) => task.engine.categoryId === id)
);

// /**
//  * Select Face Detection engines completed tasks.
//  * @returns {Array<Task>}
//  */
export const selectDetectionCompleted = createSelector(
  selectConfigEngines,
  selectCompletedTasks,
  ({ detectionCategory: id }, tasks) =>
    (tasks || []).filter((task) => task.engine.categoryId === id)
);

// /**
//  * Select Optical Tracking engines running tasks.
//  * @returns {Array<Task>}
//  */
export const selectTrackingRunning = createSelector(
  selectConfigEngines,
  selectRunningTasks,
  (
    {
      opticalTrackingEngineCatagoryId: id,
      opticalTrackingEngineCategoryId: id2,
    },
    tasks
  ) =>
    (tasks || []).filter(
      (task) => task.engine.categoryId === id || task.engine.categoryId === id2
    )
);

// /**
//  * Select Blur Redaction engines running tasks.
//  * @returns {Array<Task>}
//  */
export const selectRedactionRunning = createSelector(
  selectConfigEngines,
  selectCompletedTasks,
  selectRunningTasks,
  ({ redactEngineId: id }, completedTasks, runningTasks) => {
    const running = (runningTasks || []).filter((task) => task.engineId === id);
    const completed = (completedTasks || []).filter(
      (task) => task.engineId === id
    );

    // only return running tasks newer than last completed task
    const completedTask = completed[0];
    if (completedTask && running.length > 0) {
      return running.filter(
        (task) =>
          moment.utc(task.createdDateTime) >
          moment.utc(completedTask.createdDateTime)
      );
    }

    return running;
  }
);

// /**
//  * Select Voice Mask engines running tasks.
//  * @returns {Array<Task>}
//  * TODO combine duplicate code from similar functions into helper function
//  */
export const selectVoiceMaskRunning = createSelector(
  selectConfigEngines,
  selectCompletedTasks,
  selectRunningTasks,
  ({ voiceMaskEngineId: id }, completedTasks, runningTasks) => {
    const running = (runningTasks || []).filter((task) => task.engineId === id);
    const completed = (completedTasks || []).filter(
      (task) => task.engineId === id
    );

    // only return running tasks newer than last completed task
    const completedTask = completed[0];
    if (completedTask && running.length > 0) {
      return running.filter(
        (task) =>
          moment.utc(task.createdDateTime) >
          moment.utc(completedTask.createdDateTime)
      );
    }

    return running;
  }
);

// /**
//  * Selector to determine if head and object detections has been completed
//  * @returns {boolean}
//  */
export const selectHasHeadDetectionCompleted = createSelector(
  selectCompletedTasks,
  (completedTasks) => {
    const headDetectionTaskCompleted = (completedTasks || []).find(
      (task) =>
        task.engine.name &&
        task.engine.name.includes('Head Detection') &&
        COMPLETE_STATUSES.includes(task.status)
    );
    const outputWriterTaskCompleted = (completedTasks || []).find(
      (task) =>
        task.engine.name &&
        task.engine.name.includes('Output Writer') &&
        task.ioFolders?.some(
          (folder) => folder.referenceId === DAG_REFERENCE_IDS['owFromHead']
        ) &&
        COMPLETE_STATUSES.includes(task.status)
    );
    if (headDetectionTaskCompleted && outputWriterTaskCompleted) {
      return true;
    }
    return false;
  }
);

/**
 * Job statuses past which nothing in the job is still going. `aborted` is a
 * TaskStatus, but `Job.status` is typed `TaskStatus` in the schema too — it is
 * "computed from the statuses of its component tasks".
 */
const TERMINAL_JOB_STATUSES: ReadonlyArray<string> = [
  'complete',
  'failed',
  'cancelled',
  'aborted',
];

/**
 * Of the running tasks, the ones belonging to the latest run (VE-30784).
 *
 * The audio engines' DAG branches are two tasks — the engine, then an output
 * writer that writes its results — created together by the same job, so they
 * share a creation time. The original test kept only running tasks strictly
 * NEWER than the last completed one, so the instant the engine task completed
 * its writer failed that test and the selector reported "not running" while
 * the results were still being written. That is what left the audio tab on
 * "No engines have been run" mid-run.
 *
 * Same job as the newest completed task = the rest of that run, so keep it.
 * A newer job = a later run, keep it. Anything older = stale, drop it.
 *
 * The same-job leg is bounded by the job's own status, because `runningTasks`
 * is merely "not complete and not failed" — a task parked in `paused`,
 * `standby_pending`, `waiting` or `resuming` reads as running indefinitely,
 * and without this it would be kept forever, pinning the tab on "in progress"
 * on every future open of the file. Bounding on the job rather than on a clock
 * keeps this selector a pure function of its inputs.
 *
 * The bound is safe in the direction the platform actually lags: a job status
 * trailing its tasks still reads non-terminal, so the writer is kept — and
 * once the writer's OWN status turns terminal it leaves `runningTasks` anyway.
 * A missing `job` is treated as non-terminal, so absent data degrades to
 * keeping the task rather than re-opening the bug.
 */
const stillRunningForLatestRun = (
  running: ReadonlyArray<TdoTask>,
  completedTask: TdoTask | undefined
): ReadonlyArray<TdoTask> => {
  if (!completedTask || running.length === 0) {
    return running;
  }
  return running.filter(
    (task) =>
      (task.jobId === completedTask.jobId &&
        !TERMINAL_JOB_STATUSES.includes(task.job?.status ?? '')) ||
      moment.utc(task.createdDateTime) >
        moment.utc(completedTask.createdDateTime)
  );
};

// /**
//  * Select Transcription engines running tasks.
//  * @returns {Array<Task>}
//  */
export const selectTranscriptionRunning = createSelector(
  selectConfigEngines,
  selectCompletedTasks,
  selectRunningTasks,
  ({ transcriptionCategoryId: id }, completedTasks, runningTasks) => {
    // preferable to just use the transcription output writer if can be found
    let running = (runningTasks || []).filter((task) =>
      task.ioFolders?.some(
        (folder) =>
          folder.referenceId === DAG_REFERENCE_IDS['owFromTranscription']
      )
    );

    if (running.length === 0) {
      running = (runningTasks || []).filter(
        (task) => task.engine.categoryId === id
      );
    }

    const completed = (completedTasks || []).filter(
      (task) =>
        task.engine.categoryId === id ||
        task.ioFolders?.some(
          (folder) =>
            folder.referenceId === DAG_REFERENCE_IDS['owFromTranscription']
        )
    );

    // `newestTask`, not `completed[0]`: the task query carries no `orderBy`, so
    // array position is not recency.
    return stillRunningForLatestRun(running, newestTask(completed));
  }
);

// /**
//  * Select Transcription engines failed tasks.
//  * @returns {Array<Task>}
//  */
export const selectTranscriptionFailed = createSelector(
  selectConfigEngines,
  selectFailedTasks,
  ({ transcriptionCategoryId: id }, tasks) =>
    (tasks || []).filter((task) => task.engine.categoryId === id)
);

// /**
//  * Select Transcription engines completed tasks.
//  * @returns {Array<Task>}
//  */
export const selectTranscriptionCompleted = createSelector(
  selectConfigEngines,
  selectCompletedTasks,
  ({ transcriptionCategoryId: id }, tasks) =>
    (tasks || []).filter((task) => task.engine.categoryId === id)
);

/**
 * Matches a speaker-separation task however it was started: by its own engine
 * id (the standalone job), by category, or by the output-writer ioFolder the
 * upload DAG hangs off it — the DAG path runs under the ingestion job, so the
 * engine id alone does not find it.
 */
const makeIsSpeakerSeparationTask =
  ({
    speakerDetectionCategoryId,
    speakerSeparationEngineId,
  }: {
    speakerDetectionCategoryId?: string;
    speakerSeparationEngineId?: string;
  }) =>
  (task: TdoTask) =>
    task.engine.categoryId === speakerDetectionCategoryId ||
    task.engineId === speakerSeparationEngineId ||
    task.ioFolders?.some(
      (folder) =>
        folder.referenceId === DAG_REFERENCE_IDS['owFromSpeakerSeparation']
    );

/**
 * The latest of a set of tasks by creation date.
 *
 * Asked explicitly rather than taken as `tasks[0]`: the TDO query fetches
 * `tasks(limit:, offset:)` with no `orderBy`, so "first" is whatever the API
 * happened to return. The two selectors below used to disagree about this —
 * one indexed, the other computed — and they are answering the same question.
 */
const newestTask = (tasks: ReadonlyArray<TdoTask>): TdoTask | undefined =>
  tasks.reduce<TdoTask | undefined>(
    (latest, task) =>
      !latest ||
      moment.utc(task.createdDateTime) > moment.utc(latest.createdDateTime)
        ? task
        : latest,
    undefined
  );

/**
 * A speaker-separation run has COMPLETED on this TDO, so its results are
 * something the audio tab should expect to arrive.
 *
 * Status matters here, which is what separates this from the `hasSpeakerSeparationTask`
 * test that decides whether to FETCH (watchForTdoSuccessHandle). That one
 * deliberately ignores status — any task in the category means there may be
 * something to ask for, and asking costs one query. This one gates RENDERING,
 * and waiting on a run that is still going (the toolbar already reports it) or
 * that failed (nothing is coming) would hold a spinner over a transcript that
 * is ready to read.
 *
 * Uses the same task matcher as the running/failed selectors below, so the
 * three cannot disagree about what counts as a separation task — including the
 * upload-DAG path, which runs under the ingestion job and so is not found by
 * engine id alone.
 */
export const selectSpeakerSeparationCompleted = createSelector(
  selectConfigEngines,
  selectCompletedTasks,
  (engines, completedTasks) =>
    (completedTasks || []).some(makeIsSpeakerSeparationTask(engines))
);

/**
 * Select Speaker Separation engines running tasks.
 */
export const selectSpeakerSeparationRunning = createSelector(
  selectConfigEngines,
  selectCompletedTasks,
  selectRunningTasks,
  (engines, completedTasks, runningTasks) => {
    const isSpeakerSeparationTask = makeIsSpeakerSeparationTask(engines);

    const running = (runningTasks || []).filter(isSpeakerSeparationTask);
    const completed = (completedTasks || []).filter(isSpeakerSeparationTask);

    // Separation's DAG branch is two tasks as well — see
    // stillRunningForLatestRun for why a bare recency test is not enough.
    return stillRunningForLatestRun(running, newestTask(completed));
  }
);

/**
 * The single separation task worth polling, or undefined.
 *
 * Prefers the output writer: its completion is what means the results exist,
 * where the engine task finishing only means they are on their way.
 *
 * At most one, and scoped to ONE run. `selectSpeakerSeparationRunning` can
 * hold both tasks of a run, and tasks of more than one run when a later job is
 * in flight — so picking the first writer in array order could latch the older
 * run's writer and report success against the wrong run, array order being the
 * unordered GraphQL page (see `newestTask`). Narrow to the newest running
 * task's job first, then prefer the writer within it.
 */
export const selectSpeakerSeparationPollTask = createSelector(
  selectSpeakerSeparationRunning,
  (running) => {
    const latest = newestTask(running);
    if (!latest) {
      return undefined;
    }
    const ofLatestRun = running.filter((task) => task.jobId === latest.jobId);
    return (
      ofLatestRun.find((task) =>
        task.ioFolders?.some(
          (folder) =>
            folder.referenceId === DAG_REFERENCE_IDS['owFromSpeakerSeparation']
        )
      ) ?? ofLatestRun[0]
    );
  }
);

/**
 * Speaker separation failed, and nothing has superseded it.
 *
 * A later completed or running task clears it: a retry in flight is not a
 * failure, and a success after a failure is a success. Still the only thing
 * that reports a DAG-run failure on a media file opened after the fact: the
 * poller `lookupRunningJobs` starts for a run already going (VE-30784) only
 * covers one that is still running when the file is opened.
 */
export const selectSpeakerSeparationFailed = createSelector(
  selectConfigEngines,
  selectCompletedTasks,
  selectRunningTasks,
  selectFailedTasks,
  (engines, completedTasks, runningTasks, failedTasks) => {
    const isSpeakerSeparationTask = makeIsSpeakerSeparationTask(engines);

    const failed = (failedTasks || []).filter(isSpeakerSeparationTask);
    if (!failed.length) {
      return false;
    }

    const latestFailed = newestTask(failed);
    const superseding = [
      ...(completedTasks || []).filter(isSpeakerSeparationTask),
      ...(runningTasks || []).filter(isSpeakerSeparationTask),
    ];

    return !superseding.some(
      (task) =>
        moment.utc(task.createdDateTime) >
        moment.utc(latestFailed!.createdDateTime)
    );
  }
);

/**
 * Select Download engines running tasks.
 */
export const selectDownloadRunning = createSelector(
  selectConfigEngines,
  selectRunningTasks,
  ({ downloadEngineId: id }, tasks) =>
    (tasks || []).filter((task) => task.engineId === id)
);

export const selectAllDownloadTask = createSelector(
  selectConfigEngines,
  selectTaskHistory,
  ({ downloadEngineId: id }, tasks) =>
    (tasks || []).filter((task) => task.engineId === id)
);

export const selectAllRedactTask = createSelector(
  selectConfigEngines,
  selectTaskHistory,
  ({ redactEngineId, legacyRedactEngineId }, tasks) =>
    (tasks || []).filter(
      (task) =>
        task.engineId === redactEngineId ||
        task.engineId === legacyRedactEngineId
    )
);

export const selectVoiceMaskAssetLoading = createSelector(
  selectVoiceMaskStore,
  selectTdo,
  (voiceMaskStore, tdo) => {
    const voiceMaskJobStatus = voiceMaskStore[tdo?.id || ''] || {};

    // currently this just assumes the first job in the array the correct one - not sure if we could ever have more than one
    const voiceMaskJobsArr = Object.values(voiceMaskJobStatus);
    if (voiceMaskJobsArr.length) {
      const voiceMaskJob = voiceMaskJobsArr[0]!;
      return voiceMaskJob.isRunning; // this data structure updates the inRunning status same time asset gets loaded
    }
    return false;
  }
);

export const selectSpeakerSeparationAssetLoading = createSelector(
  selectSpeakerSeparationStore,
  selectSpeakerSeparationRunning,
  selectTdo,
  (speakerSeparationStore, runningTasks, tdo) =>
    // `?.` on the store itself, not just the per-TDO lookup: stores that don't
    // register the speaker-separation slice (component test harnesses, and any
    // host that mounts the transcript tab without it) would otherwise throw
    // here the moment anything reads this selector.
    Object.values(speakerSeparationStore?.[tdo?.id || ''] || {}).some(
      (job) => job.isRunning
    ) || runningTasks.length > 0
);
